/**
 * copieDePartie — la copie d'un en-tête ou d'un pied est-elle, dans le fichier
 * Word de l'acte, ce qu'était sa source ?
 *
 * `set_header_footer` copie la partie non demandée quand il lève la première
 * page distincte ou les pages paires (`garderLAutrePartie`,
 * `headerFooterActionExecutors.ts`). Dire « gardée » sur le seul statut de la
 * zone ne prouve pas ce que la page montre : les lectures de l'éditeur ne
 * rendent ni les champs (`PAGE`, `NUMPAGES`) ni l'alignement d'un pied
 * (`blocks.list` rend `text:'Page  / '`, relevé du 2026-10-05,
 * `tests/ws-mocked/mesure-premiere-page-distincte.spec.ts`). Le fichier exporté
 * les porte : ce module y lit les deux parties par le chemin que Word suit
 * (`word/_rels/document.xml.rels` → `word/footerN.xml`) et les compare.
 *
 * Comparé, paragraphe par paragraphe : le texte (`w:t`), les instructions de
 * champ (`w:instrText`, `w:fldSimple`), l'alignement (`w:jc`).
 *
 * Non comparé, donc jamais dit « identique » : une source qui porte une image
 * (`w:drawing`, `w:pict`, `w:object`) ou un contenu hors paragraphe simple
 * (tableau, zone de texte, contrôle de contenu). La copie d'une partie à image
 * n'a pas été mesurée (§11.7 de `superdoc-document-api.md`).
 */

import JSZip from 'jszip';

/** Ce qu'une copie relue au fichier vaut face à sa source. */
export type VerdictDeCopie =
  | { verdict: 'identique' }
  /** Les écarts, dans l'ordre où ils sont cherchés ; `paragraphes` : le nombre de paragraphes diffère. */
  | { verdict: 'differente'; ecarts: Array<'paragraphes' | 'texte' | 'champs' | 'alignement'> }
  /** La relecture ne peut pas conclure ; `raison` est la valeur portée par le reçu (`other_part.reason`). */
  | { verdict: 'non_comparable'; raison: 'source_has_image' | 'source_not_comparable' | 'file_unreadable' };

interface ParagrapheDePartie {
  texte: string;
  champs: string[];
  alignement: string | null;
}

const RELATIONS_DU_CORPS = 'word/_rels/document.xml.rels';

/** Le chemin, dans l'archive, de la partie qu'une relation du corps désigne. */
function cheminDeLaPartie(relations: string, refId: string): string | undefined {
  for (const relation of relations.matchAll(/<Relationship\b[^>]*>/g)) {
    if (relation[0].match(/\bId="([^"]+)"/)?.[1] !== refId) continue;
    const cible = relation[0].match(/\bTarget="([^"]+)"/)?.[1];
    // Une cible est relative à `word/`, ou absolue depuis la racine de l'archive (`/word/footer2.xml`).
    return cible === undefined ? undefined : `word/${cible.replace(/^\/?word\//, '')}`;
  }
  return undefined;
}

function paragraphesDe(xml: string): ParagrapheDePartie[] {
  return [...xml.matchAll(/<w:p[ >][\s\S]*?<\/w:p>/g)].map(([paragraphe]) => ({
    texte: [...paragraphe.matchAll(/<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g)].map((passage) => passage[1]).join(''),
    champs: [
      ...[...paragraphe.matchAll(/<w:instrText[^>]*>([^<]*)</g)].map((champ) => (champ[1] ?? '').trim()),
      ...[...paragraphe.matchAll(/<w:fldSimple[^>]*\bw:instr="([^"]*)"/g)].map((champ) => (champ[1] ?? '').trim()),
    ].filter((instruction) => instruction.length > 0),
    alignement: paragraphe.match(/<w:jc\b[^>]*\bw:val="([^"]+)"/)?.[1] ?? null,
  }));
}

/**
 * Compare, dans le fichier exporté de l'acte, la partie copiée à sa source.
 *
 * @param octets le fichier Word de l'acte, exporté APRÈS la copie et son rattachement.
 * @param parties les identifiants de relation (`refId`) de la source et de la copie, tels que l'éditeur les rend.
 */
export async function comparerLaCopieALaSource(
  octets: Uint8Array,
  parties: { source: string; copie: string },
): Promise<VerdictDeCopie> {
  const illisible: VerdictDeCopie = { verdict: 'non_comparable', raison: 'file_unreadable' };
  let archive: JSZip;
  try {
    archive = await JSZip.loadAsync(octets);
  } catch {
    return illisible;
  }
  const relations = await archive.file(RELATIONS_DU_CORPS)?.async('string');
  if (relations === undefined) return illisible;
  const lire = async (refId: string): Promise<string | undefined> => {
    const chemin = cheminDeLaPartie(relations, refId);
    return chemin === undefined ? undefined : archive.file(chemin)?.async('string');
  };
  const source = await lire(parties.source);
  const copie = await lire(parties.copie);
  if (source === undefined || copie === undefined) return illisible;

  if (/<(?:w:drawing|w:pict|w:object)[ >/]/.test(source)) return { verdict: 'non_comparable', raison: 'source_has_image' };
  if (/<(?:w:tbl|w:sdt|w:txbxContent|mc:AlternateContent)[ >/]/.test(source)) return { verdict: 'non_comparable', raison: 'source_not_comparable' };

  const deLaSource = paragraphesDe(source);
  const deLaCopie = paragraphesDe(copie);
  if (deLaSource.length !== deLaCopie.length) return { verdict: 'differente', ecarts: ['paragraphes'] };
  const differe = (facette: (paragraphe: ParagrapheDePartie) => string): boolean =>
    deLaSource.some((paragraphe, rang) => facette(paragraphe) !== facette(deLaCopie[rang]!));
  const ecarts: Array<'texte' | 'champs' | 'alignement'> = [
    ...(differe((paragraphe) => paragraphe.texte) ? ['texte' as const] : []),
    ...(differe((paragraphe) => paragraphe.champs.join('\u0000')) ? ['champs' as const] : []),
    ...(differe((paragraphe) => paragraphe.alignement ?? '') ? ['alignement' as const] : []),
  ];
  return ecarts.length === 0 ? { verdict: 'identique' } : { verdict: 'differente', ecarts };
}
